/* eslint-disable react/destructuring-assignment */
import Error from '../_error';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { Article, Content, ContentType } from 'pleinchamp-api-client';
import React, { createContext, RefObject, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ActuLayout } from '@actus/components/ActuLayout/ActuLayout.component';
import { usePlcApiCall } from '@api/business/api.hook';
import { ContentApi, fetchMostViewedNews } from '@api/business/api.utils';
import { MostViewedContent } from '@components/content/MostViewedContent/MostViewedContent.component';
import { FullPageLoader } from '@layout/components/FullPageLoader/FullPageLoader.component';
import { searchContent } from '@search/business/search.utils';
import { userSelectors } from '@user/business/User.store';
import { ApiStatusCode, formatPropsForStatic } from '@utils/api.utils';
import { usePlcRouter } from '@utils/customHooks';
import { defaultNamespaces } from '@utils/strings';
import { castUrlParamToString } from '@utils/url';
import { PlcPageContextProps } from 'features/business/app.types';

const NB_ACTU_MOST_VIEWED = 3;

interface Props {
  slug: string;
  article: Article | undefined;
  readAlso: Article[];
  nbComments: number;
  commentsRef: RefObject<HTMLDivElement>;
  mostViewedNews: MostViewedContent[];
  isPreview: boolean;
}

function fetchArticle(slug: Props['slug']) {
  return ContentApi.getArticle(slug).then(r => r.data);
}

function fetchReadAlso(slug: Props['slug']) {
  return ContentApi.getRelatedArticles(slug).then(r => r.data);
}

export const ActuContext = createContext<PlcPageContextProps<Props>>({
  article: { isValidating: false },
  commentsRef: { isValidating: false },
  isPreview: { isValidating: false },
  mostViewedNews: { isValidating: false },
  nbComments: { isValidating: false },
  readAlso: { isValidating: false },
  slug: { isValidating: false },
});

function ActuPage(props: Partial<Props>) {
  const { slug } = props;

  const isInit = useSelector(userSelectors.isInit);
  const isPro = useSelector(userSelectors.isPro);
  const isProArticle = Boolean(props.article?.isPro);

  const waitForArticle = isPro && isProArticle && !isInit;
  const { isValidating, error, ...restArticle } = usePlcApiCall(fetchArticle, [slug || ''], {
    // When user is pro we force reload of the article if it is a pro article to get full content
    deduping: isProArticle ? !isPro : undefined,
    initialData: props.article,
  });

  const readAlso = usePlcApiCall(fetchReadAlso, [slug || ''], {
    enabled: Boolean(slug),
    initialData: props.readAlso,
  });

  const mostViewedNews = usePlcApiCall(fetchMostViewedNews, [NB_ACTU_MOST_VIEWED], {
    initialData: props.mostViewedNews,
  });

  const commentsRef = useRef<HTMLDivElement>(null);
  const [nbComments, setNbComments] = useState<number>();

  const { isFallback } = usePlcRouter();
  if (isFallback) {
    return <FullPageLoader visible={true} />;
  }

  if (!isValidating && error?.response?.status === ApiStatusCode.notFound) {
    return <Error errorCode={ApiStatusCode.notFound} />;
  }

  return (
    <ActuContext.Provider
      value={{
        article: { ...restArticle, error, isValidating: waitForArticle || isValidating },
        commentsRef: { data: commentsRef, isValidating: false },
        isPreview: { data: props.isPreview, isValidating: false },
        mostViewedNews,
        nbComments: { data: nbComments, isValidating: false, setData: setNbComments },
        readAlso,
        slug: { data: slug, isValidating: false },
      }}
    >
      <Head>
        <meta content="max-snippet:-1, max-image-preview:large, max-video-preview:-1" name="robots" />
      </Head>
      <ActuLayout />
    </ActuContext.Provider>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  let articles: Content[];
  try {
    articles = await searchContent({ contentTypes: [ContentType.Article], isPro: false, limit: 20 }).then(r => {
      return r.contents;
    });
  } catch {
    articles = [];
  }
  const paths = articles.map(a => ({
    params: { slug: a.slug },
  }));

  return {
    fallback: true,
    paths,
  };
};

export const getStaticProps: GetStaticProps | { redirect: { destination: string; permanent: boolean } } = async ({
  params,
}) => {
  const slug = castUrlParamToString(params?.slug);

  if (!slug) {
    console.error('Error actualite StaticPropsSide, slug worth : ', slug);
    return formatPropsForStatic({});
  }
  const pageDefaultState = {
    namespacesRequired: defaultNamespaces,
    slug,
  };

  const article = await fetchArticle(slug)
    .then(result => result)
    .catch(error => {
      // ByPass - Upgrade to nextjs 10 in order to use notFound: true to handle 404 on getStaticProps.
      console.error('Error fetchArticle actualite on StaticProps', slug, error);
      if (error && error.response && error.response.status && error.response.status === ApiStatusCode.notFound) {
        return ApiStatusCode.notFound;
      }
      return undefined;
    });

  if (article === ApiStatusCode.notFound) {
    return {
      redirect: {
        destination: '/404',
        permanent: false,
      },
    };
  }
  return Promise.all([fetchReadAlso(slug), fetchMostViewedNews(NB_ACTU_MOST_VIEWED)])
    .then(([readAlso, mostViewedNews]) => ({
      ...pageDefaultState,
      article,
      mostViewedNews,
      readAlso,
    }))
    .then(props => formatPropsForStatic(props))
    .catch(error => {
      console.error('Error actualite/[slug.tsx] on StaticProps', slug, error);
      return formatPropsForStatic(pageDefaultState);
    });
};

export default ActuPage;
